import { useState } from "react";
import type { FC } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Button as MuiButton, MenuItem } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useAuth } from "react-oidc-context";
import type { SelectChangeEvent } from "@mui/material";
import { Drawer } from "../../shared/Drawer";
import type { DpiaById, DpiaShareWithType } from "../../api/dpia.types";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { CheckIcon, XIcon } from "../../shared/icons";
import { useUserInfo } from "../../api/userInfo";
import { updateDpiaShareAccessMutationOptions, updateDpiaStatusMutationOptions } from "../../api/dpia";
import { prefetchUsersFromCurrentOrganisation } from "../../api/users";
import { DpiaCollaborators } from "./DpiaCollaborators";
import { DpiaConnectedTenant } from "./DpiaConnectedTenant";
import { DpiaResponsible } from "./DpiaResponsible";
import { DpiaStatusChip } from "./DpiaStatusChip";
import { DpiaApprovalDialog } from "./DpiaApprovalDialog";
import { SendForDecisionDialog } from "./DpiaSendForDecisionDialog";
import { useMutation } from "@tanstack/react-query";
import { DynamicForm } from "../../dynamicForm";

const WIDTH = "19rem";
export const DpiaRightDrawer: FC<{ headerHeight: number | null; dpia: DpiaById }> = ({ headerHeight, dpia }) => {
  const { t } = useTranslation();
  const { formatDate } = useTranslation();

  return (
    <MuiDrawer
      anchor="right"
      variant="permanent"
      sx={{
        zIndex: 0,
        width: WIDTH,
        [`& .${drawerClasses.paper}`]: {
          width: `calc(${WIDTH} + 16px)` /** HACK: It is approax the scrollbar width. */,
          overflowY: "scroll",
          pt: `${headerHeight}px`,
        },
      }}
      data-testid="DPIA-drawer"
    >
      <Stack sx={{ gap: 3, pt: 2.5 }}>
        <Stack
          sx={{
            px: Drawer.PADDING_X,
            gap: 4,
            paddingBottom: 2,
            borderBottom: "1px solid",
            borderColor: theme => theme.palette.divider,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography variant="h6" sx={{ fontSize: theme => theme.typography.pxToRem(12) }}>
                {t("DpiaDrawer.CreatedAt.Label")}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  fontSize: theme => theme.typography.pxToRem(12),
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {formatDate(dpia.createdAt, {})}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.secondary }}
              >
                {t("DpiaDrawer.ModifiedAt.Label")}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  fontSize: theme => theme.typography.pxToRem(12),
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {dpia.updatedAt ? formatDate(dpia.updatedAt, {}) : t("DpiaDrawer.NotModified.Label")}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Stack
          sx={{
            px: Drawer.PADDING_X,
            gap: 2,
            paddingBottom: 3,
            borderBottom: "1px solid",
            borderColor: theme => theme.palette.divider,
          }}
        >
          <Box>
            <DpiaResponsible dpia={dpia} isReadOnly={!dpia.canEdit} />
          </Box>
          <Box>
            <DpiaCollaborators dpia={dpia} isReadOnly={!dpia.canEdit} />
          </Box>
          <Box>
            <DpiaConnectedTenant key={dpia.id} dpia={dpia} isReadOnly={!dpia.canEdit} />
          </Box>
          <Box>
            <Typography variant="h6">{t("DpiaDrawer.Access.Label")}</Typography>
            <AccessInput isReadOnly={!dpia.canUpdateShareWith} shareWith={dpia.shareWith} dpiaId={dpia.id} />
          </Box>
        </Stack>
        <Decision dpia={dpia} />
      </Stack>
    </MuiDrawer>
  );
};

const Decision: FC<{ dpia: DpiaById }> = ({ dpia }) => {
  const { t } = useTranslation();
  const { formatDate } = useTranslation();
  const dynamicFormState = DynamicForm.useFormState();
  const [openDialog, setOpenDialog] = useState<null | "sendForDecision" | "unsavedChanges">(null);

  const userInfo = useUserInfo();

  const handleShowSendForDecisionDialog = () => {
    if (dynamicFormState.numberOfUnsavedChanges > 0) return setOpenDialog("unsavedChanges");
    setOpenDialog("sendForDecision");
  };

  return (
    <Stack sx={{ px: Drawer.PADDING_X, gap: 2, paddingBottom: 4 }}>
      <Typography variant="h3" sx={{ fontWeight: "600" }}>
        {t("DpiaDrawer.Decision.Header")}
      </Typography>
      {(() => {
        if (dpia.approverId === userInfo.userId && dpia.status === "waitingForDecision") {
          return <ApprovalInput dpia={dpia} />;
        }

        if (dpia.status === "draft") {
          return (
            <Stack sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
              <Typography variant="body2" sx={{ fontSize: "small", color: theme => theme.palette.text.tertiary }}>
                {t("DpiaDrawer.Dialog.SendForApproval.Message")}
              </Typography>
              <Button
                size="small"
                fullWidth
                variant="outlined"
                onMouseOver={prefetchUsersFromCurrentOrganisation}
                onClick={handleShowSendForDecisionDialog}
                disabled={!dpia.canSendForApproval}
              >
                {t("DpiaDrawer.RequestDecision.Button")}
              </Button>
            </Stack>
          );
        }

        if (dpia.status === "waitingForDecision") {
          return (
            <Stack sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <DpiaStatusChip status={dpia.status} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontSize: "small", color: theme => theme.palette.text.tertiary }}>
                  <Translate
                    translationKey="DpiaDrawer.WaitingForDecision.Message"
                    values={{
                      approver: (
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {dpia.approver}
                        </Box>
                      ),
                      date: dpia.sentForApprovalDateTime && formatDate(dpia.sentForApprovalDateTime),
                    }}
                  />
                </Typography>
              </Box>
              {dpia.canSendForApproval && (
                <Button
                  size="small"
                  fullWidth
                  variant="outlined"
                  onMouseOver={prefetchUsersFromCurrentOrganisation}
                  onClick={handleShowSendForDecisionDialog}
                >
                  {t("DpiaDrawer.Dialog.SendForApprovalAgain.Button")}
                </Button>
              )}
            </Stack>
          );
        }

        return (
          <Stack sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              {dpia.status !== "preAssessment" && <DpiaStatusChip status={dpia.status} />}
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
              >
                <Translate
                  translationKey="DpiaDrawer.Decided.Message"
                  values={{
                    approver: (
                      <Box component="span" sx={{ fontWeight: 600, lineHeight: "18px" }}>
                        {dpia.approver}
                        <br />
                      </Box>
                    ),
                    date: (
                      <>
                        {dpia.actionDateTime && formatDate(dpia.actionDateTime)}
                        <br />
                      </>
                    ),
                    action: t(`DpiaDrawer.Decided.Message.Action.${dpia.status}`),
                  }}
                />
              </Typography>

              {dpia.comment && (
                <Typography
                  sx={{
                    color: theme => theme.palette.text.tertiary,
                    fontSize: "small",
                    fontStyle: "italic",
                    marginTop: 2,
                  }}
                >
                  &quot;{dpia.comment}&quot;
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              fullWidth
              variant="outlined"
              onMouseOver={prefetchUsersFromCurrentOrganisation}
              onClick={handleShowSendForDecisionDialog}
              disabled={!dpia.canSendForApproval}
            >
              {t("DpiaDrawer.RequestDecision.Button")}
            </Button>
          </Stack>
        );
      })()}
      {openDialog === "sendForDecision" && <SendForDecisionDialog dpia={dpia} onClose={() => setOpenDialog(null)} />}
      {openDialog === "unsavedChanges" && (
        <DynamicForm.UnsavedChangesDialog
          onCancel={() => setOpenDialog(null)}
          onProceed={() => setOpenDialog("sendForDecision")}
        />
      )}
    </Stack>
  );
};

const AccessInput: FC<{ isReadOnly: boolean; shareWith: DpiaShareWithType; dpiaId: string }> = ({
  isReadOnly,
  shareWith,
  dpiaId,
}) => {
  const { t } = useTranslation();
  const updateDpiaShareAccessMutation = useMutation(updateDpiaShareAccessMutationOptions());

  const handleOnChange = (event: SelectChangeEvent<DpiaShareWithType>) =>
    updateDpiaShareAccessMutation.mutate({ dpiaId, shareWith: event.target.value });

  return (
    <Box sx={{ pt: 1 }}>
      <Select
        fullWidth
        size="small"
        sx={{ fontSize: theme => theme.typography.pxToRem(12) }}
        disabled={isReadOnly || updateDpiaShareAccessMutation.isPending}
        value={shareWith}
        onChange={handleOnChange}
        data-testid="dpia-access-select"
      >
        <MenuItem value="collaborators" data-testid="dpia-access-collaborators">
          {t("DpiaDrawer.Access.Collaborators")}
        </MenuItem>
        <MenuItem value="everyone" data-testid="dpia-access-everyone">
          {t("DpiaDrawer.Access.Everyone")}
        </MenuItem>
      </Select>
    </Box>
  );
};

const ApprovalInput: FC<{ dpia: DpiaById }> = ({ dpia }) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const updateDpiaStatusMutation = useMutation(updateDpiaStatusMutationOptions());
  const { user } = useAuth();
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDisapproveDialog, setShowDisapproveDialog] = useState(false);
  const { control } = useForm<{
    comment: "";
  }>();

  const responsibleName = `${dpia.responsible.firstName} ${dpia.responsible.lastName}`;
  const comment = useWatch({ control, name: "comment" });
  const loggedInUserIsResponsibleOrCollaborator =
    dpia.responsible.userId === user?.profile.sub ||
    (dpia.collaborators && dpia.collaborators.some(c => c.userId === user?.profile.sub));

  const handleOnSuccess = () => {
    if (loggedInUserIsResponsibleOrCollaborator) return;
    navigate(paths.dpiaGrid());
  };

  return (
    <Stack>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <>
              <Typography variant="h6">{t("DpiaDrawer.Comment.Label")}</Typography>
              <TextField
                placeholder={t("DpiaDrawer.Dialog.Approve.Comment")}
                fullWidth
                inputRef={field.ref}
                onChange={field.onChange}
                multiline
                rows={5}
                size="small"
                data-testid="dpia-approval-comment"
              />
            </>
          )}
        />
        <MuiButton
          size="small"
          variant="contained"
          startIcon={<CheckIcon />}
          fullWidth
          onClick={() => setShowApproveDialog(true)}
          disabled={!dpia.canApprove}
        >
          {t("DpiaDrawer.Dialog.Approve.Button")}
        </MuiButton>
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<XIcon />}
          fullWidth
          onClick={() => setShowDisapproveDialog(true)}
          disabled={!dpia.canApprove}
        >
          {t("DpiaDrawer.Dialog.Disapprove.Button")}
        </MuiButton>
      </Box>
      {showApproveDialog && (
        <DpiaApprovalDialog
          headerText={t("DpiaDrawer.Dialog.Approve.Header")}
          message={
            <Translate
              translationKey="DpiaDrawer.Dialog.Approve.Message"
              values={{
                responsibleName: (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {responsibleName}
                  </Box>
                ),
              }}
            />
          }
          submitButtonLabel={t("DpiaDrawer.Dialog.Approve.Button")}
          onClose={() => setShowApproveDialog(false)}
          onSubmit={() =>
            updateDpiaStatusMutation.mutateAsync(
              { dpiaId: dpia.id, status: "approved", comment },
              { onSuccess: handleOnSuccess }
            )
          }
        />
      )}
      {showDisapproveDialog && (
        <DpiaApprovalDialog
          headerText={t("DpiaDrawer.Dialog.Disapprove.Header")}
          message={
            <Translate
              translationKey="DpiaDrawer.Dialog.Disapprove.Message"
              values={{
                responsibleName: (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {responsibleName}
                  </Box>
                ),
              }}
            />
          }
          submitButtonLabel={t("DpiaDrawer.Dialog.Disapprove.Button")}
          onClose={() => setShowDisapproveDialog(false)}
          onSubmit={() =>
            updateDpiaStatusMutation.mutateAsync(
              { dpiaId: dpia.id, status: "rejected", comment },
              { onSuccess: handleOnSuccess }
            )
          }
        />
      )}
    </Stack>
  );
};
